import type { LOGIN_PROVIDER_TYPE } from '@toruslabs/openlogin'
import QRCodeModal from '@walletconnect/qrcode-modal'
import {
  ADAPTER_EVENTS,
  SafeEventEmitterProvider,
  WALLET_ADAPTERS,
  WALLET_ADAPTER_TYPE
} from '@web3auth/base'
import { Web3AuthCore } from '@web3auth/core'
import { MetamaskAdapter } from '@web3auth/metamask-adapter'
import { NetworkSwitch } from '@web3auth/ui'
import { WalletConnectV1Adapter } from '@web3auth/wallet-connect-v1-adapter'
import { useRouter } from 'next/router'
import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { CHAIN_CONFIG, CHAIN_CONFIG_TYPE } from '../config/chainConfig'
import { WEB3AUTH_NETWORK_TYPE } from '../config/web3AuthNetwork'
import { getWalletProvider, IWalletProvider } from './walletProvider'

export interface IWeb3AuthContext {
  web3Auth: Web3AuthCore | null
  provider: IWalletProvider | null
  isLoading: boolean
  isMetaMask: boolean
  isPopupBlocked: boolean
  chain: string
  user: unknown
  LoggedUser: object
  web3WalletProvider: SafeEventEmitterProvider | any
  setIsLoading: (loading: boolean) => void
  login: (
    adapter: WALLET_ADAPTER_TYPE,
    provider: LOGIN_PROVIDER_TYPE,
    jwtToken: string
  ) => Promise<void>
  SocialLogin: (provider: LOGIN_PROVIDER_TYPE) => Promise<void>
  loginWithMetaMask: () => Promise<void>
  walletconnectQRCodeLogin: () => Promise<void>
  logout: () => Promise<void>
  getUserInfo: () => Promise<any>
  signMessage: (emoji: string) => Promise<any>
  metaMaskSign: () => Promise<any>
  getAccounts: () => Promise<any>
  getMaticBalance: () => Promise<any>
  getTransactions: (page: number, offset: number) => Promise<any>
  getNFTTokens: () => Promise<any>
  getMarketplaceFee: () => Promise<any>
  getGasFee: () => Promise<void>
  getMaltBalance: () => Promise<any>
  sendMaltTokens: (to: string, value: number) => Promise<any>
  signTransaction: () => Promise<void>
  sendMaticTokens: (to: string, value: number) => Promise<void>
  sendAsset: (address: string, tokenid: any) => Promise<void>
  mintAlterBody: (
    isBiconomyInitialized: any,
    biconomyProvider: any,
    contractAlter: any,
    tokenuri: string,
    data: any,
    assetId: any
  ) => Promise<any>
  mint3DBody: (
    isBiconomyInitialized: any,
    biconomyProvider: any,
    contractAlter: any,
    tokenuri: string,
    data: any,
    assetId: any
  ) => Promise<any>
  getPrivateKey: () => Promise<void>
  listForSale: (
    isErc721: any,
    nftAddress: any,
    tokenIds: any,
    price: any,
    seller: any,
    amount: any,
    erc20Address: any,
    assetId: any,
    currentCategory: string
  ) => Promise<any>
  buyNFT: (
    listingId: any,
    erc20Address: any,
    assetId: any,
    price: any
  ) => Promise<any>
  updateNFTListing: (listingId: any, assetId: any, price: any) => Promise<void>
  getListingId: (assetId: string) => Promise<any>
  cancelNFTListing: (listingId: any, assetId: any) => Promise<void>
  ApproveCheck: (currentCategory: any) => Promise<void>
  getOpenListing: () => Promise<void>
  ApproveNFT: (currentCategory: any) => Promise<void>
  getNoOfFreeMints: () => Promise<void>
}

export const Web3AuthContext = createContext<IWeb3AuthContext>({
  web3Auth: null,
  provider: null,
  isLoading: false,
  isMetaMask: false,
  isPopupBlocked: false,
  chain: '',
  user: null,
  LoggedUser: {},
  web3WalletProvider: null,
  setIsLoading: (loading: boolean) => {},
  login: async () => {},
  SocialLogin: async () => {},
  loginWithMetaMask: async () => {},
  walletconnectQRCodeLogin: async () => {},
  logout: async () => {},
  getUserInfo: async () => {},
  signMessage: async () => {},
  metaMaskSign: async () => {},
  getAccounts: async () => {},
  getMaticBalance: async () => {},
  getMaltBalance: async () => {},
  getTransactions: async () => {},
  getNFTTokens: async () => {},
  sendMaltTokens: async () => {},
  signTransaction: async () => {},
  sendMaticTokens: async () => {},
  sendAsset: async () => {},
  getGasFree: async () => {},
  mintAlterBody: async () => {},
  mint3DBody: async () => {},
  getMarketplaceFee: async () => {},
  getPrivateKey: async () => {},
  // listForSale: async () => {},
  // buyNFT: async () => {},
  cancelNFTListing: async () => {},
  updateNFTListing: async () => {},
  getListingId: async () => {},
  ApproveCheck: async () => {},
  getOpenListing: async () => {},
  ApproveNFT: async () => {},
  getNoOfFreeMints: async () => {},
  listForSale: async () => {},
  buyNFT: async () => {},
})

export function useWeb3Auth(): IWeb3AuthContext {
  return useContext(Web3AuthContext)
}

interface IWeb3AuthState {
  children?: ReactNode
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE
  chain: CHAIN_CONFIG_TYPE
}
interface IWeb3AuthProps {
  children?: ReactNode
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE
  chain: CHAIN_CONFIG_TYPE
}

export const Web3AuthProvider: FunctionComponent<IWeb3AuthState> = ({
  children,
  web3AuthNetwork,
  chain,
}: IWeb3AuthProps) => {
  const [web3Auth, setWeb3Auth] = useState<Web3AuthCore | null>(null)
  const [provider, setProvider] = useState<IWalletProvider | null>(null)
  const [user, setUser] = useState<unknown | null>(null)
  const [LoggedUser, setLoggedUser] = useState<object | {}>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isMetaMask, setIsMetaMask] = useState(false)
  const [isPopupBlocked, setIsPopupBlocked] = useState(false)
  const [appSecret, setAppSecret] = useState<any>()
  // const [redirectLink, setRedirectLink] = useState<any>();
  const [web3WalletProvider, setWeb3WalletProvider] = useState<
    SafeEventEmitterProvider | any
  >(null)
  const router = useRouter()

  const setWalletProvider = useCallback(
    async (web3authProvider: SafeEventEmitterProvider) => {
      // checking type of login, "Metamask login method didn't have eth_private key method"
      const typeOfProvider = localStorage.getItem('Web3Auth-cachedAdapter')
      if (
        typeOfProvider != 'metamask' &&
        typeOfProvider != 'wallet-connect-v1'
      ) {
        const secretKey = await web3authProvider?.request({
          method: 'eth_private_key',
        })
        // console.log("Getting secret Key ***************", secretKey);
        setAppSecret(secretKey)
      }
      setWeb3WalletProvider(web3authProvider)
      const walletProvider = getWalletProvider(chain, web3authProvider)
      setProvider(walletProvider)
    },
    [chain]
  )

  // useEffect(() => {
  //   if (provider) {
  //     const typeOfProvider = localStorage.getItem('Web3Auth-cachedAdapter')
  //     if (typeOfProvider === 'metamask') {
  //       provider.on('accountsChanged', (accounts) => console.log(accounts))
  //     }
  //   }
  // })

  useEffect(() => {
    const subscribeAuthEvents = (web3auth: Web3AuthCore) => {
      // Can subscribe to all ADAPTER_EVENTS and LOGIN_MODAL_EVENTS
      web3auth.on(ADAPTER_EVENTS.CONNECTED, async (data: unknown) => {
        console.log('connected')

        if (data?.adapter == 'metamask') {
          console.log('Yes it is')
          setIsMetaMask(true)
        }

        getUserInfo()
        setUser(data)
        setWalletProvider(web3auth.provider!)
        const method = localStorage.getItem('method')

        if (method == '1') {
          console.log('goes homepage')
          await router.push('/')
          localStorage.removeItem('method')
          setIsLoading(false)
        } else {
          if (method !== null && method !== undefined) {
            const route = JSON.parse(method)
            console.log(route?.href)
            console.log(route?.host)
            console.log('goes nft page')

            if (route?.pathname == '/login') {
              console.log('seems true')
              await router.push(route?.host)
            } else {
              await router.push(route?.href)
            }
            localStorage.removeItem('method')
            setIsLoading(false)
          }
        }
        setIsLoading(false)
      })

      web3auth.on(ADAPTER_EVENTS.CONNECTING, () => {
        // console.log("connecting");
        setIsLoading(true)
      })

      web3auth.on(ADAPTER_EVENTS.DISCONNECTED, async () => {
        // console.log("disconnected");
        localStorage.removeItem('WalletAddress')
        localStorage.removeItem('uid')
        localStorage.removeItem('signature')
        localStorage.removeItem('auth')
        localStorage.removeItem('JWT_TOKEN')
        localStorage.removeItem('REFRESH_TOKEN')
        const data = localStorage.getItem('openlogin_store')
        if (data !== null && data !== undefined) {
          const userData = JSON.parse(data)
          const sessionId = userData?.sessionId
          let openlogin_store = { sessionId: sessionId }
          localStorage.setItem(
            'openlogin_store',
            JSON.stringify(openlogin_store)
          )
        }
        if (
          window.location.pathname === '/myasset' ||
          window.location.pathname === '/login' ||
          window.location.pathname === '/wallet'
        ) {
          await router.push('/')
        }

        setUser(null)
      })

      web3auth.on(ADAPTER_EVENTS.ERRORED, (error) => {
        console.error('some error or user has cancelled login request', error)
        if (error.message === 'Unable to open window') {
          setIsPopupBlocked(true)
        }
      })
    }

    const currentChainConfig = CHAIN_CONFIG[chain]

    async function init() {
      // let redirectLink

      // const method = localStorage.getItem('method')
      // if (method !== null && method !== undefined) {
      //   if (method != '1') {
      //     redirectLink = window.location.host
      //   } else {
      //     redirectLink = window.location.href
      //   }
      // }

      // console.log('redirect url ', redirectLink)
      try {
        const { OpenloginAdapter } = await import('@web3auth/openlogin-adapter')
        const clientId: any = process.env.NEXT_PUBLIC_WEB3AUTH_CLIENT_ID
        setIsLoading(true)
        const web3AuthCoreCtorParams = {
          clientId,
          chainConfig: currentChainConfig,
        }
        const web3AuthInstance = new Web3AuthCore(web3AuthCoreCtorParams)

        const MetaMaskAdapter = new MetamaskAdapter({})

        const openLoginAdapter = new OpenloginAdapter({
          loginSettings: {
            mfaLevel: 'default',
          },
          adapterSettings: {
            network: web3AuthNetwork,
            clientId,
            // redirectUrl: redirectLink,
            uxMode: 'popup',
            loginConfig: {
              jwt: {
                name: 'testing-01',
                verifier:
                  process.env.NEXT_PUBLIC_CUSTOM_AUTH_VERIFIER || 'testing-01',
                typeOfLogin: 'jwt',
                clientId,
              },
            },
          },
        })
        const networkUi = new NetworkSwitch()
        const walletQRCodeAdapter = new WalletConnectV1Adapter({
          adapterSettings: {
            qrcodeModal: QRCodeModal,
            networkSwitchModal: networkUi,
          },
          chainConfig: currentChainConfig,
        })

        web3AuthInstance.configureAdapter(MetaMaskAdapter)
        web3AuthInstance.configureAdapter(openLoginAdapter)
        web3AuthInstance.configureAdapter(walletQRCodeAdapter)

        subscribeAuthEvents(web3AuthInstance)
        setWeb3Auth(web3AuthInstance)
        await web3AuthInstance.init()
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [chain, web3AuthNetwork, setWalletProvider])

  const login = async (
    adapter: WALLET_ADAPTER_TYPE,
    loginProvider: LOGIN_PROVIDER_TYPE,
    jwtToken: string
  ) => {
    try {
      setIsLoading(true)
      if (!web3Auth) {
        // console.log("web3auth not initialized yet");
        return
      }
      const localProvider = await web3Auth.connectTo(adapter, {
        relogin: true,
        loginProvider,
        extraLoginOptions: {
          id_token: jwtToken,
          domain: process.env.NEXT_PUBLIC_APP_DOMAIN || 'http://localhost:3000',
          verifierIdField: 'sub',
        },
      })
      setWalletProvider(localProvider!)
      // console.log(localProvider);
    } catch (error: any) {
      console.log('error', error.message)
      setIsLoading(false)
      // console.log('error', error?.code)
      // location.reload()
    }
  }

  const SocialLogin = async (loginProvider: LOGIN_PROVIDER_TYPE) => {
    try {
      setIsLoading(true)
      if (!web3Auth) {
        // console.log("web3auth not initialized yet");
        return
      }

      const web3authProvider = await web3Auth.connectTo(
        WALLET_ADAPTERS.OPENLOGIN,
        {
          relogin: true,
          loginProvider,
        }
      )
      setWalletProvider(web3authProvider!)
    } catch (error) {
      // console.log("error", error);
      setIsLoading(false)
      // location.reload()
    } finally {
      getUserInfo()
      // setIsLoading(false);
    }
  }

  const loginWithMetaMask = async () => {
    try {
      localStorage.setItem('signature', '0')
      setIsLoading(true)
      if (!web3Auth) {
        // console.log("web3auth not initialized yet");
        return
      }
      const localProvider = await web3Auth.connectTo(
        WALLET_ADAPTERS.METAMASK,
        {}
      )
      setWalletProvider(localProvider!)
    } catch (error) {
      // console.log("error", error);
    } finally {
      setIsLoading(false)
    }
  }

  const walletconnectQRCodeLogin = async () => {
    try {
      setIsLoading(true)
      if (!web3Auth) {
        // console.log("web3auth not initialized yet");
        return
      }
      const localProvider = await web3Auth.connectTo(
        WALLET_ADAPTERS.WALLET_CONNECT_V1,
        {}
      )
      setWalletProvider(localProvider!)
    } catch (error) {
      // console.log("error", error);
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    if (!web3Auth) {
      // console.log("web3auth not initialized yet");
      return
    }
    // console.log("logging out from wallet");
    // await web3Auth.logout();

    // To avoid error while session is expired but user still logged in.
    web3Auth
      .logout()
      .then(() => {
        console.log('logged out successfully')
      })
      .catch((e) => {
        // console.log(e);
        localStorage.clear()
        window.location.replace('/')
      })
    setProvider(null)
  }

  const getUserInfo = async () => {
    if (!web3Auth) {
      // console.log("web3auth not initialized yet");
      return
    }
    if (provider) {
      const user = await web3Auth.getUserInfo()
      // console.log("i got called");
      setUser(user)
      setLoggedUser(user)
      // console.log("USER INFO", await web3Auth.getUserInfo());
      return user
    }
  }

  const getAccounts = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    return provider.getAccounts()
  }

  const getMaticBalance = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const balance = await provider.getMaticBalance()
    if (balance == undefined) {
      console.warn('error occured, retrieving Matic balance again')
      const data = await provider.getMaticBalance()
      console.warn('new balance', data)
      return data
    } else {
      // console.log("Balance", balance);
      return balance
    }
  }

  const signMessage = async (emoji: string) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    // // console.log("before sign message")
    const sig = await provider.signMessage(emoji)
    // // console.log("auth", sig)
    return sig
  }

  const metaMaskSign = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    // // console.log("before sign message")
    const sig = await provider.metaMaskSign()
    // // console.log("auth", sig)
    return sig
  }

  const ApproveCheck = async (currentCategory: any) => {
    // console.log("here");
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    await provider.ApproveCheck(currentCategory)
  }

  const buyNFT = async (
    listingId: any,
    erc20Address: any,
    assetId: any,
    price: any
  ) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    let res = await provider.buyNFT(listingId, erc20Address, assetId, price)
    return res
    // console.log("buying successful")
  }

  const updateNFTListing = async (listingId: any, assetId: any, price: any) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const isTransactionSuccessFul = await provider.updateNFTListing(
      listingId,
      assetId,
      price
    )
    return isTransactionSuccessFul
    // console.log("buying successful")
  }

  const cancelNFTListing = async (listingId: any, assetId: any) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const isTransactionSuccessFul = await provider.cancelNFTListing(
      listingId,
      assetId
    )
    return isTransactionSuccessFul
    // console.log("buying successful")
  }

  const getListingId = async (assetId: string) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const result = await provider.getListingId(assetId)
    return result
    // console.log("buying successful")
  }

  const getTransactions = async (offset: number, page: number) => {
    try {
      const transactions = provider?.getTransactions(offset, page)
      return transactions
    } catch (error) {
      console.error('Error', error)
    }
  }

  const getMaltBalance = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const balance = await provider.getMaltBalance()
    return balance
  }

  const getNFTTokens = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    try {
      const nfts = await provider.getNFTTokens()
      return nfts
    } catch (error) {
      console.error('Error', error)
    }
  }

  const sendMaltTokens = async (to: string, value: number) => {
    try {
      provider?.sendMaltTokens(to, value)
    } catch (error) {
      // console.log(error);
    }
  }

  const signTransaction = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    await provider.signTransaction()
  }

  const sendMaticTokens = async (to: string, value: number) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    // // console.log("sending");
    const txDetails = await provider.sendMaticTokens(to, value)
    return txDetails
  }

  const getOpenListing = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const listings = provider.getOpenListing()
    return listings
  }

  const getNoOfFreeMints = async () => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const noFreeMints = provider.getNoOfFreeMints()
    return noFreeMints
  }

  const ApproveNFT = async (currentCategory: any) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const listings = await provider.ApproveNFT(currentCategory)
    // console.log("from web3 auth", listings);
    return listings
  }

  const mintAlterBody = async (
    isBiconomyInitialized: any,
    biconomyProvider: any,
    contractAlter: any,
    tokenuri: string,
    data: any,
    assetId: any
  ) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const mintNFT = await provider.mintAlterBody(
      isBiconomyInitialized,
      biconomyProvider,
      contractAlter,
      tokenuri,
      data,
      assetId
    )
    // console.log("from web3 auth mint", mintNFT);
    return mintNFT
  }

  const mint3DBody = async (
    isBiconomyInitialized: any,
    biconomyProvider: any,
    contractAlter: any,
    tokenuri: string,
    data: any,
    assetId: any
  ) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    const mintNFT = await provider.mint3DBody(
      isBiconomyInitialized,
      biconomyProvider,
      contractAlter,
      tokenuri,
      data,
      assetId
    )
    // console.log("from web3 auth mint", mintNFT);
    return mintNFT
  }
  const getMarketplaceFee = async () => {
    if (!provider) {
      return
    }
    const response = await provider.getMarketplaceFee()
    // console.log('feer', response)
    return response
  }

  const getGasFee = async () => {
    if (!provider) return
    const response = await provider.getGasFee()
    return response
  }

  const sendAsset = async (address: string, tokenid: any) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    await provider.sendAsset(address, tokenid)
  }

  const listForSale = async (
    isErc721: any,
    nftAddress: any,
    tokenIds: any,
    price: any,
    seller: any,
    amount: any,
    erc20Address: any,
    assetId: any,
    currentCategory: string
  ) => {
    if (!provider) {
      // console.log("provider not initialized yet");
      return
    }
    console.log(' use web3 auth', currentCategory)
    let res = await provider.listForSale(
      isErc721,
      nftAddress,
      tokenIds,
      price,
      seller,
      amount,
      erc20Address,
      assetId,
      currentCategory
    )
    return res
  }

  const getPrivateKey = async () => {
    return appSecret
  }

  const contextProvider = {
    web3Auth,
    chain,
    provider,
    user,
    isLoading,
    isMetaMask,
    isPopupBlocked,
    LoggedUser,
    setIsLoading,
    login,
    SocialLogin,
    loginWithMetaMask,
    walletconnectQRCodeLogin,
    logout,
    getUserInfo,
    getAccounts,
    getMarketplaceFee,
    getMaticBalance,
    getTransactions,
    getNFTTokens,
    getMaltBalance,
    sendMaltTokens,
    signMessage,
    metaMaskSign,
    signTransaction,
    getGasFee,
    sendMaticTokens,
    mintAlterBody,
    mint3DBody,
    sendAsset,
    getPrivateKey,
    buyNFT,
    updateNFTListing,
    listForSale,
    getListingId,
    cancelNFTListing,
    ApproveCheck,
    getOpenListing,
    ApproveNFT,
    getNoOfFreeMints,
    web3WalletProvider,
  }
  return (
    <Web3AuthContext.Provider value={contextProvider}>
      {children}
    </Web3AuthContext.Provider>
  )
}
